import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getHypeConfig, updateHypeConfig, getHypeCategories } from '../../services/hype';

// Small curated country list (ISO-3166 alpha-2). Generic across sectors/regions.
const COUNTRIES: { code: string; name: string }[] = [
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },
  { code: 'EG', name: 'Egypt' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'SG', name: 'Singapore' },
];

const LANGUAGES = [{ code: 'en', label: 'English' }, { code: 'ar', label: 'Arabic' }];

export default function HypeSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState<Record<string, string[]>>({});

  const [country, setCountry] = useState('AE');
  const [city, setCity] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>(['en']);
  const [keywords, setKeywords] = useState('');
  const [handles, setHandles] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cfg, cats] = await Promise.all([getHypeConfig(), getHypeCategories()]);
        setGroups(cats);
        setCountry(cfg.country || 'AE');
        setCity(cfg.city || '');
        setCategories(cfg.categories || []);
        setLanguages(cfg.languages?.length ? cfg.languages : ['en']);
        setKeywords((cfg.keywords || []).join(', '));
        setHandles((cfg.handles || []).join(', '));
      } catch {
        toast.error('Could not load Hype settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (list: string[], setList: (v: string[]) => void, value: string) =>
    setList(list.includes(value) ? list.filter(x => x !== value) : [...list, value]);

  const save = async () => {
    setSaving(true);
    try {
      await updateHypeConfig({
        country,
        city: city.trim() || null,
        categories,
        languages: languages.length ? languages : ['en'],
        keywords: keywords.split(',').map(k => k.trim()).filter(Boolean),
        handles: handles.split(',').map(h => h.trim()).filter(Boolean),
      });
      toast.success('Hype settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-400 py-8">Loading…</div>;
  }

  const chip = (active: boolean) =>
    `px-3 py-1.5 text-sm rounded-lg border transition-colors ${
      active ? 'bg-violet-600 text-white border-violet-600'
             : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Hype Configuration</h2>
        <p className="text-xs text-gray-500 mb-5">
          Tell Hype where and what to watch. We surface trends gaining momentum for these settings.
        </p>

        {/* Location */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
            <select value={country} onChange={e => setCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent">
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">City</label>
            <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Dubai"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
          </div>
        </div>

        {/* Languages */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Languages</label>
          <div className="flex gap-2">
            {LANGUAGES.map(l => (
              <button key={l.code} type="button" onClick={() => toggle(languages, setLanguages, l.code)}
                className={chip(languages.includes(l.code))}>{l.label}</button>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
          <div className="space-y-3">
            {Object.entries(groups).map(([group, items]) => (
              <div key={group}>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">{group}</p>
                <div className="flex flex-wrap gap-2">
                  {items.map(cat => (
                    <button key={cat} type="button" onClick={() => toggle(categories, setCategories, cat)}
                      className={chip(categories.includes(cat))}>{cat}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom keywords */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Custom keywords <span className="text-gray-400 font-normal">(optional)</span></label>
          <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)}
            placeholder="comma-separated, e.g. luxury villas, off-plan"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
          <p className="text-xs text-gray-400 mt-1">When set, these take priority over categories as search seeds.</p>
        </div>

        {/* Handles */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Tracked handles <span className="text-gray-400 font-normal">(optional)</span></label>
          <input type="text" value={handles} onChange={e => setHandles(e.target.value)}
            placeholder="@brand, @competitor"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving}
            className="px-5 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
